export const getMaxColumns = (layouts) => {
  if (!layouts?.length) return null;
  const columns = layouts
    .map(layout => parseInt(layout?.columns))
    .filter(value => Number.isInteger(value) && value > 0);
  return columns.length > 0 ? Math.max(...columns) : null;
};

export const getFormColumnCount = (formLayout = {}, allLayouts = []) => {
  const groupColumns = allLayouts?.length > 1 ? getMaxColumns(allLayouts) : null;
  return parseInt(formLayout?.columns || groupColumns) || 1;
};

export const calculateFormLayoutSpans = (selectedItems, formLayout = {}, fromExternal = false, allLayouts = []) => {
  const columnCount = getFormColumnCount(formLayout, allLayouts);

  const baseColumnSpan = Math.floor(24 / columnCount);
  const remainingSpan = 24 % columnCount;
  let regularItemIndex = 0;

  return selectedItems
    .slice()
    .sort((curr, next) => curr.order - next.order)
    .map(item => {
      const isLargeInput = item.input_type === 'file';
      let span;

      if (columnCount === 3 && isLargeInput) {
        span = 12;
      } else {
        const itemPositionInRow = regularItemIndex % columnCount;
        span = itemPositionInRow < remainingSpan ? baseColumnSpan + 1 : baseColumnSpan;
        regularItemIndex++;
      }

      return {
        ...item,
        spans: { lg: span, xs: 24 }
      };
    });
};

const normalizeString = (val) => {
  if (typeof val !== 'string') return val;
  return val.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
};

const compareValues = (inputValue, conditionValue) => normalizeString(inputValue) === normalizeString(conditionValue);

const checkIncludes = (currentValue, conditionValue) => {
  if (currentValue == null || conditionValue == null) return false;
  const normalizedConditionValue = normalizeString(conditionValue);
  
  if (Array.isArray(currentValue)) {
    return currentValue.some(value => {
      const normalizedValue = normalizeString(value);
      if (normalizedValue == null) return false;
      return String(normalizedValue).includes(String(normalizedConditionValue));
    });
  }
  
  const normalizedCurrentValue = normalizeString(currentValue);
  if (normalizedCurrentValue == null) return false;
  return String(normalizedCurrentValue).includes(String(normalizedConditionValue));
};

const isFilled = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return !!value?.toString().trim();
};

export const buildDependentLookup = (layoutConfig) => {
  const rules = layoutConfig?.logic?.visibility;
  if (!rules?.length) return {};
  return rules.reduce((lookup, rule) => {
    const { code, survey_code: surveyCode, id } = rule.target ?? {};
    if (code && surveyCode) {
      lookup[code] ??= {};
      lookup[code][surveyCode] = true;
    } else if (code) {
      lookup[code] = true;
    } else if (id != null) {
      lookup[id] = true;
    }
    return lookup;
  }, {});
};

export const isDependentField = (item, lookup) => {
  if (!lookup) return false;
  if (item.code && item.survey_code && lookup[item.code]?.[item.survey_code]) return true;
  if (item.code && lookup[item.code] === true) return true;
  if (item.id != null && lookup[item.id]) return true;
  return false;
};

export const evaluateVisibility = (item, typeName, getFieldValue, layoutConfig, patient, allItems = []) => {
  const visibilityRules = layoutConfig?.logic?.visibility;
  if (!visibilityRules?.length) return true;

  const applicableRules = visibilityRules.filter(rule => {
    if (rule.target?.type !== typeName) return false;
    if (rule.target?.code && rule.target?.survey_code) {
      return rule.target.code === item.code && rule.target.survey_code === item.survey_code;
    }
    if (rule.target?.code) {
      return rule.target.code === item.code;
    }
    return rule.target?.id === item.id;
  });

  if (applicableRules.length === 0) return true;

  return applicableRules.every(rule => {
    const conditions = rule.conditions || [];
    const logicOperator = rule.logic_operator || 'AND';

    const results = conditions.map(condition => {
      let currentValue;
      if (condition.type === 'patients') {
        currentValue = patient?.[condition.field];
      } else if (condition.code && condition.survey_code) {
        const conditionItem = allItems.find(
          item => item.code === condition.code && item.survey_code === condition.survey_code
        );
        if (conditionItem) {
          currentValue = getFieldValue?.([condition.type, conditionItem.id.toString()]);
        }
      } else if (condition.code) {
        const conditionItem = allItems.find(
          item => item.code === condition.code
        );
        if (conditionItem) {
          currentValue = getFieldValue?.([condition.type, conditionItem.id.toString()]);
        }
      } else {
        currentValue = getFieldValue?.([condition.type, condition.id.toString()]);
      }

      switch (condition.operator) {
        case '>':  return currentValue > condition.value;
        case '<':  return currentValue < condition.value;
        case '>=': return currentValue >= condition.value;
        case '<=': return currentValue <= condition.value;
        case '!=': return !compareValues(currentValue, condition.value);
        case 'includes': {
          return checkIncludes(currentValue, condition.value);
        }
        case 'not_includes': {
          return !checkIncludes(currentValue, condition.value);
        }
        case 'empty':    return !isFilled(currentValue);
        case 'not_empty': return isFilled(currentValue);
        case '==':
        default: return compareValues(currentValue, condition.value);
      }
    });

    return logicOperator === 'OR'
      ? results.some(Boolean)
      : results.every(Boolean);
  });
};

export const groupDependentItems = (rawInputs, layoutConfig) => {
  const visibilityRules = layoutConfig?.logic?.visibility || [];
  
  const childToParentKeyMap = {};
  const inputItemLookup = {};
  
  rawInputs.forEach(item => {
    if (item.code) inputItemLookup[item.code] = item;
    if (item.id != null) inputItemLookup[item.id] = item;
  });

  visibilityRules.forEach(rule => {
    const targetCode = rule.target?.code;
    const targetId = rule.target?.id;
    
    if (rule.conditions && rule.conditions.length > 0) {
      const firstCondition = rule.conditions[0];
      let parentItem = null;
      if (firstCondition.code) {
        parentItem = inputItemLookup[firstCondition.code];
      } else if (firstCondition.id != null) {
        parentItem = inputItemLookup[firstCondition.id];
      }
      
      if (parentItem) {
        const parentKey = parentItem.code || parentItem.id;
        if (targetCode) childToParentKeyMap[targetCode] = parentKey;
        if (targetId != null) childToParentKeyMap[targetId] = parentKey;
      }
    }
  });

  const topLevelFields = [];
  const dependentFieldsMap = {};

  const sortedRawInputs = [...rawInputs].sort((a, b) => a.order - b.order);

  sortedRawInputs.forEach(item => {
    const parentKey = childToParentKeyMap[item.code] || childToParentKeyMap[item.id];
    if (parentKey != null && inputItemLookup[parentKey]) {
      dependentFieldsMap[parentKey] ??= [];
      dependentFieldsMap[parentKey].push(item);
    } else {
      topLevelFields.push(item);
    }
  });

  return { topLevelItems: topLevelFields, childrenMap: dependentFieldsMap };
};


